import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import redux, { IState, ReduxMap } from '../../redux/redux';

interface IAffiliatePerson {
  Id: string;
  PersonId: number;
  FirstName: string;
  LastName: string;
  Email: string;
  Phone?: string;
  Adult: boolean;
  MaxRole?: string;
  Search?: string;
  TeacherPaid?: boolean;
  TeacherIsAttending?: boolean;
}

/**
 * Replaces AffiliateView.ascx on Default.aspx — people list for affiliate coordinators / staff.
 */
const AffiliateViewPage = (props: IState) => {
  const [people, setPeople] = useState<IAffiliatePerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    store.server
      .postApi<ServerResponse<{ People: IAffiliatePerson[] }>>('../AffiliateCoordinator/PeopleJson', {})
      .then((x) => {
        if (x.Success) {
          setPeople(x.Value?.People || []);
        } else {
          if (x.LoginRequired) redux.showLogin(true);
          toast.error(x.Message || 'Unable to load affiliate view.', { autoClose: false });
        }
      })
      .catch(() => toast.error('Unable to load affiliate view.', { autoClose: false }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (props.User) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.User]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return people.filter((p) => {
      if (!p.Adult) return false;
      if (!q) return true;
      const hay = (p.Search || `${p.LastName} ${p.FirstName} ${p.Email} ${p.Phone || ''}`).toLowerCase();
      return hay.indexOf(q) > -1;
    });
  }, [people, search]);

  if (loading) {
    return (
      <div className="main-body-page grow" data-testid="affiliate-view-page" style={{ minHeight: 120 }}>
        <h2>Affiliate View</h2>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="main-body-page grow" data-testid="affiliate-view-page" style={{ minHeight: 120 }}>
      <div className="flex-between">
        <h2>Affiliate View</h2>
        <button type="button" className="btn btn-default btn-xs" data-testid="affiliate-view-refresh" onClick={load}>
          <i className="fa fa-refresh" /> Refresh
        </button>
      </div>
      <div className="form-horizontal">
        <div className="form-group">
          <label className="control-label col-sm-2" htmlFor="av-search">
            Search
          </label>
          <div className="col-sm-6">
            <input
              id="av-search"
              type="text"
              className="form-control"
              data-testid="affiliate-view-search"
              placeholder="search by name, email, etc"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>
      <br />
      <table className="table table-hover table-striped" data-testid="affiliate-view-table">
        <thead>
          <tr>
            <th />
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Role</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {filtered.map((x) => {
            const role = x.MaxRole || 'Adult';
            let icons: React.ReactNode = null;
            if (x.MaxRole === 'Teacher') {
              icons = (
                <>
                  <span className={x.TeacherPaid ? 'text-success' : 'text-danger'}>
                    <i className="fa fa-money" />
                  </span>
                  &nbsp;&nbsp;
                  <span className={x.TeacherIsAttending ? 'text-success' : 'text-danger'}>
                    <i className={x.TeacherIsAttending ? 'fa fa-check-square-o' : 'fa fa-square-o'} />
                  </span>
                </>
              );
            }
            return (
              <tr key={x.PersonId} data-testid={`affiliate-view-row-${x.PersonId}`}>
                <td>
                  <a
                    className="btn btn-default btn-xs"
                    href={StoreX.BuildUrlWithFair(`../app/user/${x.Id}/`)}
                    target="_blank"
                    rel="noreferrer"
                    title="Edit"
                  >
                    <i className="fa fa-edit" />
                  </a>
                </td>
                <td>
                  {x.LastName} {x.FirstName}
                </td>
                <td>{x.Email}</td>
                <td>{x.Phone}</td>
                <td>{role}</td>
                <td className="text-right">
                  <strong>{icons}</strong>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {filtered.length === 0 && (
        <div className="alert alert-info" data-testid="affiliate-view-empty">
          No adults found.
        </div>
      )}
      <div className="head-room" data-testid="affiliate-view-legend">
        Paid: <span className="text-success"><i className="fa fa-money" /></span>
        <br />
        Not Paid: <span className="text-danger"><i className="fa fa-money" /></span>
        <br />
        Attending: <span className="text-success"><i className="fa fa-check-square-o" /></span>
        <br />
        Not Attending: <span className="text-danger"><i className="fa fa-square-o" /></span>
      </div>
    </div>
  );
};

export default ReduxMap(AffiliateViewPage);
